/**
 * Faz 6 — dimensional constraint command runners (Y2): DCALIGNED/
 * DCHORIZONTAL/DCVERTICAL/DCRADIUS/DCDIAMETER/DCANGULAR create the
 * constraint AND its dynamic dimension entity in ONE undo step (OCS
 * set.dimensions binding), DCFORM rebinds a constraint to a named
 * parameter expression, DIMCONSTRAINT/DCCONVERT converts an existing
 * associative dimension, PARAMETERS opens the parameter table dialog.
 */

import { useAppStore, type Entity } from '../../../state/store';
import { useConstraintStore } from '../../constraints/constraintStore';
import { undoableConstraints } from '../../constraints/constraintOps';
import { solveConstraintSet, syncBoundDimensions, type SolveResult } from '../../constraints/solve';
import { evaluateExpression } from '../../constraints/parameters';
import { isDimensionalKind } from '../../constraints/kinds';
import { nearestWritableMarker, nearestPolylineSegment, resolveMarkerPoint } from '../../constraints/attach';
import { MARKER_CENTER, MARKER_RADIUS, type ConstraintPayload, type ConstraintRef } from '../../constraints/types';
import { runInteraction, PK, PTxt } from './lib';
import { makeEntity } from '../entityOps';
import { useCommandInteract } from '../commandInteract';
import { useDimStyleStore } from '../../../tools/cad/DimensioningTools';
import { lineIntersection2 } from '../geometryMath';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';

/** Serialize the constraint set (undo snapshot). */
const snapshot = (): ConstraintPayload => useConstraintStore.getState().serializePayload();

/**
 * Record a dimensional constraint + its dynamic dimension entity as ONE
 * undoable step, bind them (OCS set.dimensions) and re-solve.
 */
function commitDimensional(
    label: string,
    kind: 'Distance' | 'DistanceX' | 'DistanceY' | 'Angle' | 'Radius' | 'Diameter',
    refs: ConstraintRef[],
    value: number,
    dimEntity: Entity
): { id: string; result: SolveResult } {
    const before = snapshot();
    const cid = useConstraintStore.getState().addConstraint({
        kind,
        refs,
        driving: { literal: value },
        value,
    });
    useConstraintStore.getState().bindDimension(cid, dimEntity.id);
    useAppStore.getState().addEntity(dimEntity);
    const after = snapshot();
    useAppStore.getState().pushHistory({
        type: 'cmd.dcConstraint',
        timestamp: Date.now(),
        data: { label },
        undo: () => {
            const a = useAppStore.getState();
            a.deleteEntity(dimEntity.id);
            useConstraintStore.getState().applyPayload(before);
        },
        redo: () => {
            const a = useAppStore.getState();
            a.addEntity(dimEntity);
            useConstraintStore.getState().applyPayload(after);
        },
    });
    const result = solveConstraintSet();
    if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
    return { id: cid, result };
}

/** Dimension text anchor: run midpoint offset perpendicular (aligned) or up. */
function textAnchor(pa: Pt3, pb: Pt3, aligned: boolean): Pt3 {
    const mx = (pa[0] + pb[0]) / 2, my = (pa[1] + pb[1]) / 2;
    if (!aligned) return [mx, my + 0.6, pa[2] ?? 0];
    const dx = pb[0] - pa[0], dy = pb[1] - pa[1];
    const len = Math.hypot(dx, dy) || 1;
    return [mx - (dy / len) * 0.6, my + (dx / len) * 0.6, pa[2] ?? 0];
}

function conflictSuffix(ids: string[], result: SolveResult): string {
    return ids.length > 0 && result.conflicts.includes(ids[0])
        ? ' — recorded, but it conflicts with the existing set.'
        : '.';
}

/** Shared flow: linear/aligned distance from two picked points, with the
 *  Faz 4 "Object" option chip restarting as a single-line pick flow. */
function startDcDistance(mode: 'aligned' | 'horizontal' | 'vertical'): void {
    const cmd = mode === 'aligned' ? 'DCALIGNED' : mode === 'horizontal' ? 'DCHORIZONTAL' : 'DCVERTICAL';
    const kind = mode === 'aligned' ? 'Distance' : mode === 'horizontal' ? 'DistanceX' : 'DistanceY';
    const commitFromRefs = (pa: Pt3, pb: Pt3, refs: ConstraintRef[]): string => {
        const measured = mode === 'aligned'
            ? Math.hypot(pb[0] - pa[0], pb[1] - pa[1])
            : mode === 'horizontal'
                ? Math.abs(pb[0] - pa[0])
                : Math.abs(pb[1] - pa[1]);
        if (measured < 1e-9) return `${cmd}: degenerate measurement.`;
        const dim = makeEntity('dimension', `Dim-${kind}-${Date.now()}`, {
            type: mode === 'aligned' ? 'aligned' : 'linear',
            point1: [...pa],
            point2: [...pb],
            measured,
            textPosition: textAnchor(pa, pb, mode === 'aligned'),
            styleId: useDimStyleStore.getState().activeStyleId,
        });
        const { id, result } = commitDimensional(cmd, kind, refs, measured, dim);
        return `${cmd}: ${kind} constraint ${id} = ${measured.toFixed(2)} added with its dimension${conflictSuffix([id], result)}`;
    };
    const segPoint = (entity: Entity, world: [number, number]): { pa: Pt3; pb: Pt3; refs: ConstraintRef[] } | null => {
        if (entity.type === 'line') {
            const a = resolveMarkerPoint(entity, 0), b = resolveMarkerPoint(entity, 1);
            if (!a || !b) return null;
            return {
                pa: a, pb: b,
                refs: [{ entityId: entity.id, marker: 0 }, { entityId: entity.id, marker: 1 }],
            };
        }
        if (entity.type === 'polyline' || entity.type === 'polygon') {
            const seg = nearestPolylineSegment(entity, world);
            const vs = (Array.isArray((entity.data as Record<string, any>).vertices) ? (entity.data as Record<string, any>).vertices : []) as Pt3[];
            if (seg === null || vs.length < 2) return null;
            const j = (seg + 1) % vs.length;
            return {
                pa: [vs[seg][0], vs[seg][1], vs[seg][2] ?? 0],
                pb: [vs[j][0], vs[j][1], vs[j][2] ?? 0],
                refs: [{ entityId: entity.id, marker: seg }, { entityId: entity.id, marker: j }],
            };
        }
        return null;
    };

    runInteraction({
        steps: [
            { prompt: `${cmd}: pick first point (or Object for a whole line)`, kind: 'pick', acceptTypes: ['line', 'polyline', 'polygon', 'circle', 'arc'], options: [{ keyword: 'Object' }] },
            { prompt: `${cmd}: pick second point`, kind: 'pick', acceptTypes: ['line', 'polyline', 'polygon', 'circle', 'arc'] },
        ],
        onOption: (keyword) => {
            if (keyword.toUpperCase() !== 'OBJECT') return;
            useCommandInteract.getState().cancelActive();
            runInteraction({
                steps: [{ prompt: `${cmd}: pick a line/segment`, kind: 'pick', acceptTypes: ['line', 'polyline', 'polygon'] }],
                complete: (_p, _v, picks, pickPoints) => {
                    const { entities } = useAppStore.getState();
                    const entity = entities[picks[0]];
                    const wp = (pickPoints ?? [])[0];
                    const seg = entity && wp ? segPoint(entity, [wp.x, wp.y]) : null;
                    if (!seg) return `${cmd}: pick a line or a polyline segment.`;
                    return commitFromRefs(seg.pa, seg.pb, seg.refs);
                },
            });
        },
        complete: (_p, _v, picks, pickPoints) => {
            const pts = pickPoints ?? [];
            const { entities } = useAppStore.getState();
            const toRefPoint = (index: number): { point: Pt3; ref: ConstraintRef } | null => {
                const entity = entities[picks[index]];
                if (!entity) return null;
                const wp = pts[index];
                const world: [number, number] = wp ? [wp.x, wp.y] : [0, 0];
                const hit = nearestWritableMarker(entity, world);
                if (!hit) return null;
                return { point: hit.point, ref: { entityId: entity.id, marker: hit.marker } };
            };
            const a = toRefPoint(0);
            const b = toRefPoint(1);
            if (!a || !b) return `${cmd}: pick two pointable objects.`;
            return commitFromRefs(a.point, b.point, [a.ref, b.ref]);
        },
    });
}

export const constraintDimRunners: RunnerMap = {
    'par.dcAligned': () => {
        startDcDistance('aligned');
        return 'DCALIGNED: pick first point (Object = whole line)';
    },
    'par.dcHorizontal': () => {
        startDcDistance('horizontal');
        return 'DCHORIZONTAL: pick first point (Object = whole line)';
    },
    'par.dcVertical': () => {
        startDcDistance('vertical');
        return 'DCVERTICAL: pick first point (Object = whole line)';
    },

    'par.dcRadius': () => {
        runInteraction({
            steps: [PK('DCRADIUS: pick a circle/arc', ['circle', 'arc'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                const c = e ? resolveMarkerPoint(e, MARKER_CENTER) : null;
                const rp = e ? resolveMarkerPoint(e, MARKER_RADIUS) : null;
                if (!e || !c || !rp) return 'DCRADIUS: pick a circle/arc.';
                const r = Math.hypot(rp[0] - c[0], rp[1] - c[1]);
                const dim = makeEntity('dimension', `Dim-radius-${Date.now()}`, {
                    type: 'radius',
                    center: [...c],
                    radiusPoint: [...rp],
                    radius: r,
                    styleId: useDimStyleStore.getState().activeStyleId,
                });
                const refs: ConstraintRef[] = [
                    { entityId: e.id, marker: MARKER_CENTER },
                    { entityId: e.id, marker: MARKER_RADIUS },
                ];
                const { id, result } = commitDimensional('DCRADIUS', 'Radius', refs, r, dim);
                return `DCRADIUS: Radius constraint ${id} = ${r.toFixed(2)} added with its dimension${conflictSuffix([id], result)}`;
            },
        });
        return 'DCRADIUS: pick a circle/arc';
    },

    'par.dcDiameter': () => {
        runInteraction({
            steps: [PK('DCDIAMETER: pick a circle/arc', ['circle', 'arc'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                const c = e ? resolveMarkerPoint(e, MARKER_CENTER) : null;
                const rp = e ? resolveMarkerPoint(e, MARKER_RADIUS) : null;
                if (!e || !c || !rp) return 'DCDIAMETER: pick a circle/arc.';
                const r = Math.hypot(rp[0] - c[0], rp[1] - c[1]);
                const dim = makeEntity('dimension', `Dim-diameter-${Date.now()}`, {
                    type: 'diameter',
                    center: [...c],
                    radiusPoint: [...rp],
                    radius: r,
                    styleId: useDimStyleStore.getState().activeStyleId,
                });
                const refs: ConstraintRef[] = [
                    { entityId: e.id, marker: MARKER_CENTER },
                    { entityId: e.id, marker: MARKER_RADIUS },
                ];
                const { id, result } = commitDimensional('DCDIAMETER', 'Diameter', refs, r * 2, dim);
                return `DCDIAMETER: Diameter constraint ${id} = ${(r * 2).toFixed(2)} added with its dimension${conflictSuffix([id], result)}`;
            },
        });
        return 'DCDIAMETER: pick a circle/arc';
    },

    'par.dcAngular': () => {
        runInteraction({
            steps: [PK('DCANGULAR: pick first line', ['line', 'polyline', 'polygon']), PK('DCANGULAR: pick second line', ['line', 'polyline', 'polygon'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const segOf = (id: string): [Pt3, Pt3] | null => {
                    const e = entities[id];
                    if (!e) return null;
                    const a = resolveMarkerPoint(e, 0), b = resolveMarkerPoint(e, 1);
                    return a && b ? [a, b] : null;
                };
                const s1 = segOf(picks[0]), s2 = segOf(picks[1]);
                if (!s1 || !s2) return 'DCANGULAR: pick two lines.';
                const d1 = [s1[1][0] - s1[0][0], s1[1][1] - s1[0][1]];
                const d2 = [s2[1][0] - s2[0][0], s2[1][1] - s2[0][1]];
                const cos = (d1[0] * d2[0] + d1[1] * d2[1]) / (Math.hypot(d1[0], d1[1]) * Math.hypot(d2[0], d2[1]) || 1);
                const deg = Math.abs(Math.acos(Math.max(-1, Math.min(1, cos))) * 180 / Math.PI);
                const vertex = lineIntersection2(
                    { a: s1[0], b: s1[1] },
                    { a: s2[0], b: s2[1] }
                ) ?? ([(s1[0][0] + s2[0][0]) / 2, (s1[0][1] + s2[0][1]) / 2, s1[0][2] ?? 0] as Pt3);
                const dim = makeEntity('dimension', `Dim-angular-${Date.now()}`, {
                    type: 'angular',
                    center: [...vertex],
                    point1: [...s1[1]],
                    point2: [...s2[1]],
                    angle: deg,
                    textPosition: [vertex[0] + 1, vertex[1] + 1, vertex[2] ?? 0],
                    override: `${deg.toFixed(1)}°`,
                    styleId: useDimStyleStore.getState().activeStyleId,
                });
                const refs: ConstraintRef[] = [
                    { entityId: picks[0], marker: 0 },
                    { entityId: picks[0], marker: 1 },
                    { entityId: picks[1], marker: 0 },
                    { entityId: picks[1], marker: 1 },
                ];
                const { id, result } = commitDimensional('DCANGULAR', 'Angle', refs, deg, dim);
                return `DCANGULAR: Angle constraint ${id} = ${deg.toFixed(1)}° added with its dimension${conflictSuffix([id], result)}`;
            },
        });
        return 'DCANGULAR: pick first line';
    },

    // --------------------------------------------------------------
    // DCFORM (Faz 6.9): bind a dimensional constraint to a parameter
    // expression (DrivingValue.Named) through the ParameterTable.
    // --------------------------------------------------------------
    'par.dcForm': () => {
        const selected = useConstraintStore.getState().selectedConstraintId;
        const store = useConstraintStore.getState();
        const selectedRecord = selected ? store.set.constraints.find((c) => c.id === selected) : undefined;
        const applyExpression = (cid: string, expr: string): string => {
            const current = useConstraintStore.getState();
            const rec = current.set.constraints.find((c) => c.id === cid);
            if (!rec || !isDimensionalKind(rec.kind)) return 'DCFORM: the target constraint is not dimensional.';
            try {
                evaluateExpression(expr, current.set.parameters);
            } catch (error) {
                return `DCFORM: ${(error as Error).message}`;
            }
            const prefix = rec.kind === 'Radius' ? 'radius' : rec.kind === 'Diameter' ? 'diameter' : rec.kind === 'Angle' ? 'angle' : 'd';
            let max = 0;
            for (const name of Object.keys(current.set.parameters)) {
                const m = new RegExp(`^${prefix}(\\d+)$`).exec(name);
                if (m) max = Math.max(max, Number(m[1]));
            }
            const paramName = `${prefix}${max + 1}`;
            undoableConstraints(() => {
                const s = useConstraintStore.getState();
                s.setParameter(paramName, { expression: expr });
                s.updateConstraint(cid, { driving: { named: paramName } });
            }, 'DCFORM');
            const result = solveConstraintSet();
            if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
            return `DCFORM: ${cid} now driven by ${paramName} = ${expr}${result.conflicts.includes(cid) ? ' (conflicting)' : ''}.`;
        };

        if (selectedRecord && isDimensionalKind(selectedRecord.kind)) {
            runInteraction({
                steps: [PTxt(`DCFORM: expression for ${selectedRecord.id} (e.g. width*2)`)],
                complete: (_p, values) => {
                    const expr = String(values[0] ?? '').trim();
                    if (!expr) return 'DCFORM: empty expression.';
                    return applyExpression(selectedRecord.id, expr);
                },
            });
            return `DCFORM: expression for ${selectedRecord.id}`;
        }
        runInteraction({
            steps: [PK('DCFORM: pick a constraint-driven dimension', ['dimension']), PTxt('DCFORM: enter expression (e.g. width*2)')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const dim = entities[picks[0]];
                const cid = typeof (dim?.data as Record<string, any>)?.constraintDriven === 'string'
                    ? (dim.data as Record<string, any>).constraintDriven as string
                    : null;
                if (!cid) return 'DCFORM: pick a constraint-driven dimension (create one with a DC* command).';
                const expr = String(values[1] ?? '').trim();
                if (!expr) return 'DCFORM: empty expression.';
                return applyExpression(cid, expr);
            },
        });
        return 'DCFORM: pick a constraint-driven dimension';
    },

    // --------------------------------------------------------------
    // DIMCONSTRAINT / DCCONVERT (Faz 6.10): convert an existing
    // associative dimension into a driving constraint. Parameter names
    // follow the OCS sequence (d1, d2… / radius1… / diameter1…) and the
    // dimension becomes the constraint's dynamic dimension (set.dimensions).
    // Converted/invalid candidates are refused like OCS counts them.
    // --------------------------------------------------------------
    'par.dim': () => {
        runInteraction({
            steps: [PK('DIMCONSTRAINT: pick an associative dimension to convert', ['dimension'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const dim = entities[picks[0]];
                if (!dim || dim.type !== 'dimension') return 'DCCONVERT: pick a dimension.';
                const data = dim.data as Record<string, any>;
                if (typeof data.constraintDriven === 'string') {
                    return `DCCONVERT: dimension already drives constraint ${data.constraintDriven} (converted dimensions are refused).`;
                }
                const srcId = (Array.isArray(data.assoc) ? data.assoc[0] : null) as string | null;
                const src = srcId ? entities[srcId] : null;
                if (!src) return 'DCCONVERT: dimension has no association to convert (DIMREASSOCIATE first).';

                const refAt = (p: number[] | undefined, used: number[]): ConstraintRef | null => {
                    if (!Array.isArray(p) || p.length < 2) return null;
                    let best: { marker: number } | null = null;
                    let bestDist = Infinity;
                    for (const marker of markerPool(src)) {
                        if (used.includes(marker)) continue;
                        const q = resolveMarkerPoint(src, marker);
                        if (!q) continue;
                        const dist = Math.hypot(q[0] - p[0], q[1] - p[1]);
                        if (dist < bestDist) { bestDist = dist; best = { marker }; }
                    }
                    return best ? { entityId: src.id, marker: best.marker } : null;
                };

                const current = useConstraintStore.getState();
                const nextName = (prefix: string): string => {
                    let max = 0;
                    for (const name of Object.keys(current.set.parameters)) {
                        const m = new RegExp(`^${prefix}(\\d+)$`).exec(name);
                        if (m) max = Math.max(max, Number(m[1]));
                    }
                    return `${prefix}${max + 1}`;
                };

                let kind: 'Distance' | 'DistanceX' | 'DistanceY' | 'Radius' | 'Diameter';
                let value = 0;
                let refs: ConstraintRef[] = [];
                let prefix = 'd';
                const dimType = String(data.type ?? '');
                if (dimType === 'aligned' || dimType === 'linear' || dimType === 'baseline' || dimType === 'continue') {
                    const r1 = refAt(data.point1, []);
                    const r2 = refAt(data.point2, r1 ? [r1.marker] : []);
                    if (!r1 || !r2) return 'DCCONVERT: could not resolve the dimension endpoints on the associated geometry.';
                    refs = [r1, r2];
                    const dx = Math.abs((data.point2 as number[])[0] - (data.point1 as number[])[0]);
                    const dy = Math.abs((data.point2 as number[])[1] - (data.point1 as number[])[1]);
                    if (dimType === 'linear') {
                        kind = dx >= dy ? 'DistanceX' : 'DistanceY';
                        value = dx >= dy ? dx : dy;
                    } else {
                        kind = 'Distance';
                        value = Math.hypot(dx, dy);
                    }
                } else if (dimType === 'radius' || dimType === 'diameter' || dimType === 'jogged') {
                    if (!RADIAL2.includes(src.type)) return 'DCCONVERT: radial dimensions need an associated circle/arc.';
                    kind = dimType === 'diameter' ? 'Diameter' : 'Radius';
                    prefix = dimType === 'diameter' ? 'diameter' : 'radius';
                    const c = resolveMarkerPoint(src, MARKER_CENTER);
                    const rp = resolveMarkerPoint(src, MARKER_RADIUS);
                    if (!c || !rp) return 'DCCONVERT: could not resolve the radial geometry.';
                    const r = Math.hypot(rp[0] - c[0], rp[1] - c[1]);
                    value = dimType === 'diameter' ? r * 2 : r;
                    refs = [
                        { entityId: src.id, marker: MARKER_CENTER },
                        { entityId: src.id, marker: MARKER_RADIUS },
                    ];
                } else {
                    return `DCCONVERT: ${dimType || 'unknown'} dimensions are not convertible (angular needs two associated lines).`;
                }

                const paramName = nextName(prefix);
                const before = snapshot();
                const cid = useConstraintStore.getState().addConstraint({
                    kind,
                    refs,
                    driving: { named: paramName },
                    value,
                });
                useConstraintStore.getState().setParameter(paramName, { expression: String(value) });
                useConstraintStore.getState().bindDimension(cid, dim.id);
                const dimBefore = structuredClone(dim.data) as Record<string, any>;
                const dimAfter = { ...dimBefore, constraintDriven: cid } as Record<string, any>;
                useAppStore.getState().updateEntity(dim.id, { data: dimAfter });
                const after = snapshot();
                useAppStore.getState().pushHistory({
                    type: 'cmd.dcConvert',
                    timestamp: Date.now(),
                    data: { label: 'DCCONVERT', constraint: cid },
                    undo: () => {
                        useAppStore.getState().updateEntity(dim.id, { data: dimBefore });
                        useConstraintStore.getState().applyPayload(before);
                    },
                    redo: () => {
                        useAppStore.getState().updateEntity(dim.id, { data: dimAfter });
                        useConstraintStore.getState().applyPayload(after);
                    },
                });
                const result = solveConstraintSet();
                if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
                return `DCCONVERT: dimension converted — constraint ${cid} (${kind}) driven by ${paramName} = ${value.toFixed(2)}${result.conflicts.includes(cid) ? ' (conflicting)' : ''}.`;
            },
        });
        return 'DIMCONSTRAINT: pick an associative dimension';
    },

    // --------------------------------------------------------------
    // PARAMETERS (Faz 6.9): the parameter table dialog.
    // --------------------------------------------------------------
    'par.manager': () => {
        useAppStore.getState().openManageDialog('parameters');
        return 'PARAMETERS: parameter table opened.';
    },
};

/** Writable marker pool for DCCONVERT ref matching. */
function markerPool(entity: Entity): number[] {
    if (entity.type === 'line') return [0, 1];
    if (entity.type === 'circle' || entity.type === 'arc') return [MARKER_CENTER, MARKER_RADIUS];
    if (entity.type === 'polyline' || entity.type === 'polygon') {
        const vs = (Array.isArray((entity.data as Record<string, any>).vertices) ? (entity.data as Record<string, any>).vertices : []) as Pt3[];
        return Array.from({ length: Math.max(0, vs.length) }, (_, i) => i);
    }
    return [];
}

const RADIAL2 = ['circle', 'arc'];
